import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PageHeader } from '@platform/ui-kit';
import { api, type AirPlatform, type AirPlatformAssessment } from '../../../api';

/**
 * Strategic Intelligence → AI Platforms Catalog → one platform.
 *
 * Ported from `views/platforms/detail.ejs`. Section order is the source's: the
 * positioning/summary band, "How it runs / deploys / learning curve", the At-a-glance
 * heatmap, then Good for / Less good for.
 *
 * `favColor` below is the source's function reproduced exactly, thresholds and hex values
 * unchanged, so a rating paints the same colour here as it does there. Getting that
 * subtly wrong would misreport a platform's capability, which is the one thing this
 * screen exists to convey.
 *
 * ── THE LOWER HALF ─────────────────────────────────────────────────────────────────────
 *
 * Coverage, Reference and "Ask about this platform" were missing and are now here, cloned
 * from `views/platforms/detail.ejs:142-205`. `covColor` is the source's five-stop scale
 * reproduced exactly — a coverage cell must paint the same colour here as it does there,
 * for the same reason `favColor` must.
 */

/** The source's covColor() — detail.ejs:13-21, thresholds and hexes unchanged. */
function covColor(score: unknown): { bg: string; fg: string } {
  const v = Number(score);
  if (!v) return { bg: '#E0E0E0', fg: '#757575' };
  if (v >= 5) return { bg: '#1A9850', fg: '#fff' };
  if (v >= 4) return { bg: '#91CF60', fg: '#1b3a1b' };
  if (v >= 3) return { bg: '#FEE08B', fg: '#5d4037' };
  if (v >= 2) return { bg: '#FC8D59', fg: '#3a1f0a' };
  return { bg: '#D73027', fg: '#fff' };
}

interface CoverageCell {
  id: string;
  label: string;
  score: number;
  rationale?: string;
}

/** The scale, spelled out beneath the grid exactly as the source spells it out. */
const COV_LEGEND: [number, string][] = [
  [5, 'Best-in-class'],
  [4, 'Strong'],
  [3, 'Adequate'],
  [2, 'Weak'],
  [1, 'Minimal'],
];

/** The source's favColor() — same thresholds, same hexes. */
function favColor(rating: unknown): { bg: string; fg: string } {
  const r = Number(rating);
  if (r >= 5) return { bg: '#2e7d32', fg: '#fff' };
  if (r >= 4) return { bg: '#66bb6a', fg: '#fff' };
  if (r >= 3) return { bg: '#fdd835', fg: '#5d4037' };
  if (r >= 2) return { bg: '#fb8c00', fg: '#fff' };
  return { bg: '#e53935', fg: '#fff' };
}

/** The source's construct tiles, in its order and with its labels. */
const CONSTRUCT_FIELDS: ReadonlyArray<[string, string]> = [
  ['execution', 'How it runs'],
  ['deployment', 'How it deploys'],
  ['learning', 'Learning curve'],
];

function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * `platformId` arrives as a PROP, not from useParams.
 *
 * The module's routes are enumerated by depth (`/ai_readiness/:a/:b/:c/:d/:e`) because
 * React Router ranks a splat below a dynamic segment, so the params are positional and
 * carry no meaning. The module already resolves the record id off the path; passing it
 * down beats re-deriving it here from a param whose name depends on nesting depth.
 */
export function AiPlatformDetail({ platformId }: { platformId: string }) {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState<AirPlatform | null>(null);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  /* Coverage shows one axis at a time — the source toggles rather than stacking, because
     10 + 17 cells at once stops being a heat map. */
  const [covView, setCovView] = useState<'function' | 'industry'>('function');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);


  useEffect(() => {
    if (!platformId) return;
    let active = true;
    setLoading(true);
    api.aiReadiness
      .getPlatform(platformId)
      .then((d) => { if (active) setPlatform(d.platform); })
      .catch((e) => { if (active) setError(e?.message || 'Could not load the platform.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [platformId]);

  const backToCatalog = () => navigate('/ai_readiness/strategic-intelligence/ai-platforms-catalog');

  if (loading) return <Typography sx={{ fontSize: 13, color: '#999' }}>Loading…</Typography>;

  if (error || !platform) {
    return (
      <>
        <Typography sx={{ fontSize: 13, color: 'error.main', mb: 2 }}>
          {error || 'Platform not found.'}
        </Typography>
        <Button onClick={backToCatalog}>← Back to AI Platforms</Button>
      </>
    );
  }

  const profile = platform.profile ?? {};
  const construct = (profile.construct ?? {}) as Record<string, string>;
  const assessment: AirPlatformAssessment[] = Array.isArray(profile.assessment)
    ? profile.assessment
    : [];
  // The source falls back from good_for to pros, and from less_good_for to cons.
  /* detail.ejs:24-26 — both axes come off `profile.coverage`, written by generateCoverage. */
  const cov = (profile.coverage ?? {}) as { function?: CoverageCell[]; industry?: CoverageCell[] };
  const fnCov: CoverageCell[] = Array.isArray(cov.function) ? cov.function : [];
  const indCov: CoverageCell[] = Array.isArray(cov.industry) ? cov.industry : [];
  const covCells = covView === 'industry' ? indCov : fnCov;

  /* detail.ejs:179-183 — the four reference fields, in the source's order. */
  const reference: [string, string][] = (
    [
      ['cost_model', 'Cost model'],
      ['security', 'Security & compliance'],
      ['erp_connectivity', 'ERP connectivity'],
      ['differentiators', 'Differentiators'],
    ] as [string, string][]
  ).filter(([k]) => profile[k]);

  const ask = () => {
    const q = question.trim();
    if (!q) return;
    setAsking(true);
    setAskError(null);
    setAnswer('Searching the web…');
    api.aiReadiness
      .askPlatform(platformId, q)
      .then((d) => setAnswer(d.answer || '(no answer)'))
      .catch((e: Error) => {
        setAnswer(null);
        setAskError(e.message);
      })
      .finally(() => setAsking(false));
  };

  const pull = () => {
    setPulling(true);
    setAskError(null);
    api.aiReadiness
      .refreshPlatform(platformId)
      /* Re-read rather than patch in place: refresh MERGES over the stored profile server
         side, so the row is the only thing that knows the result. */
      .then(() => api.aiReadiness.getPlatform(platformId))
      .then((d) => setPlatform(d.platform))
      .catch((e: Error) => setAskError(e.message))
      .finally(() => setPulling(false));
  };

  const goodFor = asStringList(profile.good_for).length
    ? asStringList(profile.good_for)
    : asStringList(profile.pros);
  const lessGoodFor = asStringList(profile.less_good_for).length
    ? asStringList(profile.less_good_for)
    : asStringList(profile.cons);
  const summary = (profile.summary as string) || (profile.overview as string) || '';
  const positioning = (profile.positioning as string) || '';

  return (
    <>
      <PageHeader title={platform.name} description={platform.category || ''} />

      <Box sx={{ mb: 2 }}>
        <Button size="small" onClick={backToCatalog} sx={{ fontSize: 13 }}>
          ← Back to AI Platforms
        </Button>
      </Box>

      {positioning || summary ? (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: '16px 20px' }}>
            {positioning ? (
              <Typography sx={{ fontSize: '1.05rem', m: '0 0 6px', fontWeight: 700 }}>
                {positioning}
              </Typography>
            ) : null}
            {summary ? (
              <Typography sx={{ color: '#424242', m: 0 }}>{summary}</Typography>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {CONSTRUCT_FIELDS.some(([k]) => construct[k]) ? (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            mb: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          }}
        >
          {CONSTRUCT_FIELDS.filter(([k]) => construct[k]).map(([k, label]) => (
            <Card key={k} sx={{ height: '100%' }}>
              <CardContent sx={{ p: '14px 16px' }}>
                <Typography sx={{ color: '#424242', fontWeight: 600, fontSize: 14 }}>
                  {label}
                </Typography>
                <Typography sx={{ color: '#616161', m: '8px 0 0', fontSize: '0.92rem' }}>
                  {construct[k]}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : null}

      {assessment.length ? (
        <>
          <Typography sx={{ color: '#424242', fontWeight: 600, m: '18px 0 10px' }}>
            At a glance
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: '10px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            }}
          >
            {assessment.map((a, i) => {
              const col = favColor(a.rating);
              return (
                <Box
                  key={`${a.area ?? 'area'}-${i}`}
                  sx={{ background: col.bg, color: col.fg, borderRadius: '8px', p: '12px 14px' }}
                >
                  <Box sx={{ fontSize: '0.74rem', opacity: 0.9, letterSpacing: '0.3px' }}>
                    {a.area}
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '1.1rem', m: '2px 0 6px' }}>
                    {a.label || ''}
                  </Box>
                  <Box sx={{ fontSize: '0.78rem', opacity: 0.92, lineHeight: 1.25 }}>
                    {a.note || ''}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      ) : (
        <Box sx={{ p: '10px 16px', background: '#fff8e1', color: '#8d6e00', borderRadius: 1 }}>
          No at-a-glance profile generated yet.
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: '18px',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        <Card sx={{ borderTop: '3px solid #2e7d32' }}>
          <CardContent>
            <Typography sx={{ fontSize: '1.05rem', color: '#2e7d32', fontWeight: 500, mb: 1 }}>
              Good for
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 3, color: '#424242' }}>
              {goodFor.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </Box>
            {profile.best_fit ? (
              <Typography sx={{ color: '#616161', fontSize: '0.9rem' }}>
                {String(profile.best_fit)}
              </Typography>
            ) : null}
          </CardContent>
        </Card>

        <Card sx={{ borderTop: '3px solid #e53935' }}>
          <CardContent>
            <Typography sx={{ fontSize: '1.05rem', color: '#e53935', fontWeight: 500, mb: 1 }}>
              Less good for
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 3, color: '#424242' }}>
              {lessGoodFor.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ── Coverage — detail.ejs:142-176 ─────────────────────────────────────── */}
      {fnCov.length || indCov.length ? (
        <Box sx={{ mt: 3.5 }}>
          <Typography sx={{ fontWeight: 600, color: '#424242', mb: 1.25 }}>Coverage</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#757575', fontSize: '0.9rem', mr: 0.5 }}>View by:</Typography>
            <Button
              size="small"
              variant={covView === 'function' ? 'contained' : 'outlined'}
              onClick={() => setCovView('function')}
            >
              Function
            </Button>
            <Button
              size="small"
              variant={covView === 'industry' ? 'contained' : 'outlined'}
              onClick={() => setCovView('industry')}
            >
              Industry
            </Button>
            <Typography sx={{ color: '#757575', fontSize: '0.85rem', ml: 1 }}>
              Fit 1–5 · hover a cell for the rationale
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: 1,
            }}
          >
            {covCells.map((cell) => {
              const c = covColor(cell.score);
              return (
                <Box
                  key={cell.id}
                  title={cell.rationale || ''}
                  sx={{
                    background: c.bg,
                    color: c.fg,
                    borderRadius: '7px',
                    padding: '10px 12px',
                    cursor: 'default',
                  }}
                >
                  <Box sx={{ fontSize: '0.78rem', lineHeight: 1.2, minHeight: 30 }}>
                    {cell.label}
                  </Box>
                  <Box sx={{ fontSize: '1.4rem', fontWeight: 700, mt: 0.5 }}>
                    {cell.score || '—'}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.75, alignItems: 'center', mt: 1.5 }}>
            {COV_LEGEND.map(([score, label]) => {
              const c = covColor(score);
              return (
                <Box key={score} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 16, height: 16, borderRadius: '3px', background: c.bg }} />
                  <Typography sx={{ fontSize: '0.8rem', color: '#616161' }}>
                    {score} · {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {/* ── Reference — detail.ejs:178-186 ────────────────────────────────────── */}
      {reference.length ? (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography sx={{ fontSize: '1rem', color: '#424242', fontWeight: 500, mb: 1 }}>
              Reference
            </Typography>
            {reference.map(([key, label]) => (
              <Typography key={key} sx={{ my: 0.75, color: '#424242' }}>
                <strong>{label}:</strong> {String(profile[key])}
              </Typography>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {/* ── Ask about this platform — detail.ejs:188-205 ──────────────────────── */}
      <Card sx={{ mt: 3, background: '#eceff1' }}>
        <CardContent>
          <Typography sx={{ fontSize: '1.05rem', color: '#37474f', fontWeight: 500 }}>
            Ask about this platform
          </Typography>
          <Typography sx={{ color: '#616161', mb: 1.25 }}>
            Ask a specific question — the AI assistant will search the web for current details.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="e.g. What's the latest pricing? Does it run air-gapped? Recent product changes?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            sx={{ background: '#fff', mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={ask} disabled={asking || !question.trim()}>
              {asking ? 'Searching…' : 'Ask'}
            </Button>
            {/* The source pairs Ask with a full re-profile, because the same web search that
                answers one question can refresh the whole record. */}
            <Button onClick={pull} disabled={pulling}>
              {pulling ? 'Pulling…' : 'Pull latest data'}
            </Button>
          </Box>
          {askError ? (
            <Typography sx={{ mt: 2, color: '#c62828' }}>{askError}</Typography>
          ) : null}
          {answer ? (
            <Box
              sx={{
                mt: 2,
                p: '14px 16px',
                background: '#fff',
                borderRadius: '8px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {answer}
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}
